import {
  CAMPAIGN_OPERATIONS_PHASES,
  CampaignOperationsKind,
  type CampaignOperationsHistoryEntry,
  type CampaignOperationsPhase,
} from "@/marketing/models/campaignOperations";
import type { InfluencerCampaign, InfluencerCampaignStatus } from "@/marketing/models/influencerCampaign";

export interface CampaignTrackerDeadline {
  phase: CampaignOperationsPhase;
  dueDate: string;
  daysLeft: number;
}

export interface CampaignTrackerPace {
  remaining: number;
  perDay: number;
}

export type CampaignTrackerTraffic = "red" | "yellow" | "green" | "none";

export interface CampaignTrackerFilter {
  kind: CampaignOperationsKind;
  status?: InfluencerCampaignStatus;
  pic?: string;
  search?: string;
}

// Phases in these states no longer carry a deadline; Paused is on hold, not late.
const SETTLED_STATUSES = new Set(["Completed", "Not Needed", "Paused"]);
const AT_RISK_POSTING = new Set(["At Risk", "Off Track"]);
const YELLOW_WITHIN_DAYS = 3;
const DAY_MS = 86_400_000;

/** Derives the master-sheet display columns (deadline, traffic light, pace) from a campaign. */
export class CampaignTrackerRows {
  public static nextDeadline(campaign: InfluencerCampaign, today: string): CampaignTrackerDeadline | null {
    const operations = campaign.operations;
    if (operations?.cancelled) return null;

    let earliest: CampaignTrackerDeadline | null = null;
    for (const phase of CAMPAIGN_OPERATIONS_PHASES) {
      const state = operations?.phases?.[phase];
      if (state?.status && SETTLED_STATUSES.has(state.status)) continue;
      const dueDate = state?.dueDate ?? (phase === "posting" ? CampaignTrackerRows.calendarDay(campaign.postingDueDate) : undefined);
      if (!dueDate) continue;
      if (!earliest || dueDate < earliest.dueDate) {
        earliest = { phase, dueDate, daysLeft: CampaignTrackerRows.daysBetween(today, dueDate) };
      }
    }
    return earliest;
  }

  public static traffic(campaign: InfluencerCampaign, today: string): CampaignTrackerTraffic {
    if (campaign.operations?.cancelled) return "none";
    const postingStatus = campaign.operations?.phases?.posting?.status;
    if (postingStatus && AT_RISK_POSTING.has(postingStatus)) return "red";
    const deadline = CampaignTrackerRows.nextDeadline(campaign, today);
    if (!deadline) return "none";
    if (deadline.daysLeft < 0) return "red";
    return deadline.daysLeft <= YELLOW_WITHIN_DAYS ? "yellow" : "green";
  }

  /** Posts still needed per day across the remaining posting days, today included. */
  public static postingPace(campaign: InfluencerCampaign, today: string): CampaignTrackerPace | null {
    const target = campaign.postTarget;
    const dueDate = campaign.operations?.phases?.posting?.dueDate ?? CampaignTrackerRows.calendarDay(campaign.postingDueDate);
    if (!target || !dueDate) return null;
    const remaining = Math.max(target - (campaign.postedCount ?? 0), 0);
    if (remaining === 0) return { remaining: 0, perDay: 0 };
    const daysLeft = CampaignTrackerRows.daysBetween(today, dueDate) + 1;
    if (daysLeft <= 0) return null;
    return { remaining, perDay: Math.ceil(remaining / daysLeft) };
  }

  public static matches(campaign: InfluencerCampaign, filter: CampaignTrackerFilter): boolean {
    const operations = campaign.operations;
    const kind = operations?.kind ?? CampaignOperationsKind.Standard;
    if (kind !== filter.kind) return false;
    if (filter.status && campaign.status !== filter.status) return false;

    const pics = [operations?.pics?.brand, operations?.pics?.scouting, operations?.pics?.content].filter((pic): pic is string => !!pic);
    const pic = filter.pic?.trim().toLowerCase();
    if (pic && !pics.some((name) => name.toLowerCase() === pic)) return false;

    const search = filter.search?.trim().toLowerCase();
    if (search && ![campaign.name, ...pics].some((text) => text.toLowerCase().includes(search))) return false;
    return true;
  }

  public static changedFieldsSince(campaign: InfluencerCampaign, sinceIso: string): string[] {
    const fields = (campaign.operations?.history ?? []).filter((entry) => entry.at >= sinceIso).map((entry) => entry.field);
    return [...new Set(fields)];
  }

  public static lastChange(campaign: InfluencerCampaign, field: string): CampaignOperationsHistoryEntry | undefined {
    const history = campaign.operations?.history ?? [];
    for (let index = history.length - 1; index >= 0; index -= 1) {
      if (history[index]?.field === field) return history[index];
    }
    return undefined;
  }

  private static calendarDay(timestamp: string | undefined): string | undefined {
    return timestamp ? timestamp.slice(0, 10) : undefined;
  }

  private static daysBetween(fromDay: string, toDay: string): number {
    return Math.round((Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS);
  }
}
